import React from "react";
import { useInView } from "react-intersection-observer";

export interface ResponsiveLazyBoxProps {
  aspectRatioDefault: string; // e.g., "16 / 9"
  aspectRatioSm?: string; // e.g., "3 / 2" // min - 640
  aspectRatioMd?: string; // e.g., "4 / 3" // min - 992
  aspectRatioLg?: string; // e.g., "1 / 1" // min -1200
  rootMargin?: string; // Intersection observer root margin
  maxHeight?: string; // e.g., "400px", "50vh"
  children?: React.ReactNode;
  bannerType?: string;
  positionInfo?: string;
  bannerName?: string;
  ctaName?: string;
}

const ResponsiveLazyBox: React.FC<ResponsiveLazyBoxProps> = ({
  aspectRatioDefault,
  aspectRatioSm,
  aspectRatioMd,
  aspectRatioLg,
  rootMargin = "200px 0px",
  maxHeight = "none",
  children,
  // Analytics props
  bannerType,
  positionInfo,
  bannerName,
  ctaName
}) => {
  // Fallback logic: each breakpoint falls back to the previous one if not set
  const sm = aspectRatioSm || aspectRatioDefault;
  const md = aspectRatioMd || sm;
  const lg = aspectRatioLg || md;

  const style = {
    "--aspect-ratio-default": aspectRatioDefault,
    "--aspect-ratio-sm": sm,
    "--aspect-ratio-md": md,
    "--aspect-ratio-lg": lg,
    "--responsive-lazybox-max-height": maxHeight
  } as React.CSSProperties;

  const tailwindClasses = `
    w-full text-white flex items-center justify-center
    [aspect-ratio:var(--aspect-ratio-default)]
    sm:[aspect-ratio:var(--aspect-ratio-sm)]
    md:[aspect-ratio:var(--aspect-ratio-md)]
    lg:[aspect-ratio:var(--aspect-ratio-lg)]
    [max-height:var(--responsive-lazybox-max-height)]
    [&>div:has(>.aem-carousel)]:w-full
  `;

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin
  });

  // Build analytics data attributes based on the props
  const analyticsAttributes = React.useMemo(() => {
    const attributes: Record<string, string> = {};

    if (bannerType) {
      attributes["data-bannertype"] = bannerType;
    }

    if (positionInfo) {
      attributes["data-positioninfo"] = positionInfo;
    }

    if (bannerName) {
      attributes["data-bannername"] = bannerName;
    }

    if (ctaName) {
      attributes["data-ctaname"] = ctaName;
    }

    return attributes;
  }, [bannerType, positionInfo, bannerName, ctaName]);
  return (
    <div
      ref={ref}
      style={style}
      className={tailwindClasses}
      {...analyticsAttributes}
    >
      {inView ? children : null}
    </div>
  );
};

export default ResponsiveLazyBox;
